/*
 * © 2017 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
import { t } from "i18next";
import {
  OffsetChangeDetected,
  OffsetAdjustmentApplied,
  OffsetAdjustmentCalculation,
  MachinePatch,
  IPCRunMode,
  OffsetControlLimits,
  ProcessAction,
  ProcessUpdatesTableType,
  DetailsAction,
} from "@centralwebteam/narwhal";

import { AlertLevel, levelColours } from "@centralwebteam/jellyfish";
import constants from "@/constants";
import { getProcessUpdateAlertLevel } from "./utils";

export class ProcessUpdatePresentation {
  sourceMachine: MachinePatch | undefined;
  targetMachine:
    | {
        id: string;
        make: string;
        name: string;
        type: string;
        model: string;
        serialNumber: string;
      }
    | undefined;
  controlledOffset: string;
  controlledOffsetType: string;
  displayName: string | undefined;
  offsetUsage: number | undefined;
  correlationId: string;
  /** Present only while/after an offset adjustment apply request is in flight for this row. */
  requestId?: string;

  /** Original event data for offset application */
  rawEvent:
    | OffsetAdjustmentApplied
    | OffsetAdjustmentCalculation
    | OffsetChangeDetected
    | ProcessAction;

  offsetUpdate?: number;
  lastOffsetAdjustment?: string;

  measuredFeature?: string;
  measuredCharacteristic?: string;
  toleranceType?: string;
  jobName: string | undefined;
  jobId: string | undefined;
  time: Date | undefined;

  offsetUsageFill?: string;
  fillOffsetUpdate?: string;

  runMode?: IPCRunMode;

  address: string | number | undefined;
  measurementVerdict: string | undefined;
  offsetValue?: number;

  // used in process updates page for graphic tooltip
  maxCumulativeLimit?: number;
  baselineToolOffset?: number;

  // used to show offset icon
  offsetTypeLevel: AlertLevel;

  controllerUnits?: string;
  grouping: ProcessUpdatesTableType;

  constructor(
    event:
      | OffsetAdjustmentApplied
      | OffsetAdjustmentCalculation
      | OffsetChangeDetected
      | ProcessAction
  ) {
    const { subType, details, correlationId } = event;
    const offsetType = details?.offsetTarget?.type || "ToolOffset";
    const address =
      offsetType === "MacroOffset"
        ? details?.offsetTarget?.variableNumber
        : (details?.offsetTarget?.toolNumber ??
          details?.offsetTarget?.toolName);
    const controlledOffsetName = details?.offsetTarget?.id;
    const offsetNameProps = {
      offsetName: controlledOffsetName,
      targetType: offsetType,
      target: details?.offsetTarget?.target,
    };
    this.address = address;
    this.displayName = details?.action?.name;
    this.correlationId = correlationId ?? "";
    this.requestId = event.requestId;
    this.rawEvent = event;
    this.controlledOffset = controlledOffsetMapper(offsetNameProps);
    this.controlledOffsetType = offsetType;
    this.toleranceType =
      event?.details?.processTrigger?.characteristic.toleranceType;
    this.time = new Date(event?.created);
    this.grouping = event.grouping ?? "Machine";

    if (details) {
      const {
        sourceMachine,
        processTarget,
        offsetLifeCycle,
        offsetControlLimits,
        processTrigger,
        analysisTrigger,
        executionSettings,
        action,
      } = details;

      this.jobName = analysisTrigger?.name;
      this.jobId = analysisTrigger?.id;
      this.sourceMachine = sourceMachine;
      this.targetMachine = processTarget;
      this.measurementVerdict =
        processTrigger?.analysisOutcome?.measurementVerdict ?? "Pass";
      if (action?.units === "Dimensionless") {
        this.controllerUnits = "";
      } else {
        this.controllerUnits = action?.units ?? "";
      }

      this.offsetUpdate = getOffsetAdjustment(action, subType);
      this.offsetValue = getOffsetValue(action, subType, event.dispatchMethod);

      this.offsetUsage = offsetLifeCycle?.wearPercentage;
      this.maxCumulativeLimit =
        offsetControlLimits?.maxCumulativeLimit?.nativeValue ??
        offsetControlLimits?.maxCumulativeLimit?.value;
      this.baselineToolOffset =
        details?.offsetBaseline?.nativeValue ?? details?.offsetBaseline?.value;

      this.runMode = executionSettings?.runMode
        ? (executionSettings.runMode as IPCRunMode)
        : ("Auto" as IPCRunMode);
      this.measuredCharacteristic = processTrigger?.characteristic?.name;

      if (processTrigger?.characteristic?.featureGroups?.length) {
        this.measuredFeature = processTrigger.characteristic.featureGroups
          .map((feature: any) => feature.name)
          .join(", ");
      } else {
        this.measuredFeature = processTrigger?.characteristic?.featureName;
      }
    }

    const eventIsOffsetAppliedType =
      !!subType &&
      typeof event.details?.offsetLifeCycle?.wearPercentage === "number" &&
      constants.processActionsOffsetAdjustmentOffsetTypes.includes(
        subType.toLowerCase()
      );

    if (eventIsOffsetAppliedType) {
      this.offsetUsageFill = getOffsetUsageFill(event);
    }
    this.lastOffsetAdjustment = getLastOffsetAdjustment(
      subType,
      details?.offsetControlLimits
    );
    this.offsetTypeLevel = getProcessUpdateAlertLevel(subType, event.details);
    this.fillOffsetUpdate = levelColours[this.offsetTypeLevel];
  }
}

function getOffsetValue(
  action: DetailsAction | undefined,
  subType: string,
  dispatchMethod: string | undefined
) {
  if (
    subType === "MachineAdjustmentApplied" ||
    subType === "MachineAdjustmentCalculation" ||
    subType === "OffsetAdjustmentCalculation"
  ) {
    return action?.cncProposedNativeValue ?? action?.cncProposedValue;
  }

  if (subType === "OffsetChangeDetected") {
    return action?.cncReadingNativeValue ?? action?.cncReadingValue;
  }

  if (subType === "OffsetAdjustmentApplied") {
    if (dispatchMethod === "FileDrop") {
      if (action?.updateMode === "Incremental") {
        return action?.cncChangeNativeValue ?? action?.cncChangeValue;
      }
      if (action?.updateMode === "Absolute") {
        return action?.cncProposedNativeValue ?? action?.cncProposedValue;
      }
    } else {
      return action?.cncProposedNativeValue ?? action?.cncProposedValue;
    }
  }

  return action?.cncChangeNativeValue ?? action?.cncChangeValue;
}

function getOffsetAdjustment(
  action: DetailsAction | undefined,
  subType: string
) {
  if (
    subType === "OffsetChangeDetected" ||
    subType === "OffsetAdjustmentCalculation"
  ) {
    return action?.cncChangeNativeValue ?? action?.cncChangeValue;
  }
  if (subType === "OffsetAdjustmentApplied") {
    if (action?.updateMode === "Incremental") {
      return action?.cncChangeNativeValue ?? action?.cncChangeValue;
    }
    if (action?.updateMode === "Absolute") {
      return action?.cncProposedNativeValue ?? action?.cncProposedValue;
    }
  }

  return action?.cncProposedNativeValue ?? action?.cncProposedValue;
}

function getLastOffsetAdjustment(
  subType: string,
  offsetControlLimits?: OffsetControlLimits
) {
  if (
    subType === "OffsetAdjustmentCalculation" ||
    subType === "MachineAdjustmentCalculation"
  ) {
    return "Calculated";
  }
  if (subType === "OffsetChangeDetected") {
    return t("OffsetStatus.Detected");
  }

  if (
    offsetControlLimits?.maxIncrementalLimit.exceeded === false &&
    offsetControlLimits?.maxCumulativeLimit.exceeded === false
  ) {
    return t("OffsetStatus.Applied");
  } else if (offsetControlLimits?.maxIncrementalLimit.exceeded) {
    return t("OffsetStatus.Capped");
  } else if (offsetControlLimits?.maxCumulativeLimit.exceeded) {
    return t("OffsetStatus.Exceeded");
  }

  return t("OffsetStatus.Applied");
}

function getOffsetUsageFill(event: ProcessAction): string {
  if (
    event.details?.offsetLifeCycle &&
    event.details.offsetLifeCycle?.wearPercentage > constants.offsetErrorMin
  ) {
    return levelColours[AlertLevel.offsetError];
  } else if (
    event.details?.offsetLifeCycle &&
    event.details?.offsetLifeCycle?.wearPercentage >=
      constants.offsetWarningMin &&
    event.details.offsetLifeCycle?.wearPercentage <= constants.offsetWarningMax
  ) {
    return levelColours[AlertLevel.offsetWarning];
  } else {
    return levelColours[AlertLevel.offset];
  }
}

type OffsetNameProps = {
  targetType: string;
  offsetName: string | undefined;
  target?: string;
};
const controlledOffsetMapper = (p: OffsetNameProps) => {
  let name = "--";
  if (p.targetType === "ToolOffset") {
    name = `TO#${p.offsetName ?? "?"}`;
  } else if (p.targetType === "MacroOffset") {
    name = `#${p.offsetName ?? "?"}`;
  } else if (p.targetType === "GCodeOffset") {
    name = p.target ?? "?";
  }

  return name;
};
